import { useQuery } from '@apollo/client'
import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Button, CenteredLoader, Input } from '@/components_deprecated'
import { PermissionsRequired } from '@/containers/permission-containers'
import { formatISO } from 'date-fns'
import uniq from 'lodash/uniq'
import type { ExportIssueForm } from '@/modules/issues'
import {
  ALL_ISSUE_RISK_ASPECTS_QUERY,
  ExportIssuePrompt,
  GET_ALL_ISSUE_SUBCATEGORIES,
} from '@/modules/issues'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import type { IdVariable } from '@/types/graphql'
import type { Coordinates } from '@/types/graphql/maps'
import type { Nullable } from '@/types/util'
import { format } from '@/utils/date-fns'
import type { FileAttachment } from '@/utils/hooks'
import {
  useBreadcrumbs,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import type { EditIssueForm } from '..'
import { ISSUE_QUERY } from '..'
import type { AttachmentsRef } from '../components'
import { Attachments, Details, IssueTagPicker, MainInfo } from '../components'
import { TagBadge } from '../components/tag-picker/tag-badge'
import {
  IssueRelationsPanel,
  Map,
  buildInitialRelationOptions,
  serializeRelationOptions,
  useRequiredRelationsSatisfied,
} from '../components'
import {
  useExportIssuesMutations,
  usePatchIssueMutation,
} from '../mutations.hooks'
import type {
  AllIssueRiskAspectsQuery,
  AllIssueRiskAspectsQueryVariables,
  IssueQuery,
  PatchIssueInput,
} from '../types.graphl'
import {
  IssueFormField,
  buildIssueFormValidators,
  omitHiddenIssueInput,
  useIssueFieldConfig,
} from '../field-config'
import { IssueActionLogTimeline } from './issue-action-log'
import { pollForNewActionLog } from './poll-for-new-action-log'
import { H2 } from '@/components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};

  /* Leave room for the sticky mobile action bar. */
  ${props => props.theme.media.mobile} {
    padding-bottom: 6rem;
  }
`

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  /* On mobile the two columns flatten into a single stack (display: contents)
     so individual cards can be reordered — the metadata card is lifted to sit
     right after the title (see CardTitle / MainInfo order rules). */
  ${props => props.theme.media.custom({ max: '1100px' })} {
    flex-direction: column;
    align-items: stretch;
  }
`
const MainColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${props => props.theme.media.custom({ max: '1100px' })} {
    display: contents;
  }
`
const Rail = styled.aside`
  flex: 0 0 340px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${props => props.theme.media.custom({ max: '1100px' })} {
    display: contents;
  }
`
const CardTitle = styled.div`
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  background: white;
  box-shadow: var(--shadow-card);

  > .inner {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 600px;
  }

  h2 {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #78716c;
  }
  p {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #1c1917;
  }

  ${props => props.theme.media.custom({ max: '1100px' })} {
    order: -2;
  }
`
const Loading = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background-color: rgba(255, 255, 255, 0.5);
`

interface IssueProps {}

export const Issue: React.FC<IssueProps> = () => {
  const translations = useTranslate({
    issue: 'common.issue',
    title: 'issues.title',
    loading: 'common.loading-ellipsis',
    edit: 'common.edit',
    cancel: 'common.cancel',
    save: 'common.save',

    open: 'common.open-alt',
    closed: 'common.closed',
    archived: ['common.archived', val => val.toLowerCase()],

    status: 'common.status',
    category: 'common.category',
    responsible: 'common.responsible',
    date: 'common.date',
    reference: ['issues.reference', { abbreviate: false }],
    regNr: 'common.reg-nr',
    about: 'common.about',

    export: 'common.export',

    validation: {
      solutionRequired: 'issues.validation.solution-required',
      required: 'common.required',
    },
  })

  const issueId = useParam('issueId')

  const attachmentsRef = useRef<AttachmentsRef>(null)

  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const location = useLocation()

  const me = useUser()
  const { setOverride } = useBreadcrumbs()
  const addPrompt = usePrompt()
  const onErrorAuto = useOnErrorAuto()
  const fieldConfig = useIssueFieldConfig()

  const [editing, setEditing] = useState(!!location.state?.edit)
  const [coordinates, setCoordinates] = useState<Nullable<Coordinates>>({
    lat: null,
    lng: null,
  })
  const [attachmentsAdd, setAttachmentsAdd] = useState<
    Omit<FileAttachment, 'id'>[]
  >([])
  const [attachmentsRemove, setattachmentsRemove] = useState<string[]>([])

  const validateSolution = (
    solution: string | undefined,
    form: EditIssueForm
  ) => {
    if (form.closed && solution?.trim() === '')
      return translations.validation.solutionRequired
    return null
  }

  const {
    formValues: form,
    formErrors: errors,
    formValid,
    formEdited,
    resetForm,
    updateForm,
    updateInitialValues,
    formChangeHandler: handler,
    submitHandler: submit,
    validateForm,
  } = useForm<EditIssueForm>({
    values: {
      user: '',
      responsibleUser: '',
      caseHandler: '',
      closed: false,
      archived: false,
      category: '',
      date: new Date(),
      dateEnd: new Date(),

      placename: '',
      regNr: '',
      title: '',
      description: '',
      damage: '',
      reason: '',
      locationDescription: '',
      immediateAction: '',
      action: '',

      estimatedCost: 0,
      actualCost: 0,
      suggestedSolution: '',
      solution: '',
      severity: null,
      riskAspect: null,
      processChoice: null,
      subcategory: '',
      relationOptions: {},
      tags: [],
    },
    validators: {
      // Which fields are mandatory is a per-company setting; these come from
      // IssueFieldConfig. Only the close-out rule stays hardcoded.
      ...buildIssueFormValidators(fieldConfig, 'edit', {
        requiredMessage: translations.validation.required,
        hasRiskAspects: () => hasRiskAspects,
        hasSubcategories: () => hasSubcategories,
      }),
      solution: validateSolution,
    },
    config: {
      onlyValidateAfterFirstUpdate: false,
    },
  })

  const {
    data,
    loading: queryLoading,
    error: queryError,
    refetch,
  } = useQuery<IssueQuery, IdVariable>(ISSUE_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: issueId,
    },
    onCompleted: handleData,
    onError: onErrorAuto(),
  })

  const { data: subcategories, loading: subcategoriesLoading } = useQuery(
    GET_ALL_ISSUE_SUBCATEGORIES,
    {
      variables: { categoryId: form.category ?? '' },
      skip: !form.category,
      fetchPolicy: 'network-only',
      onError: onErrorAuto(),
    }
  )

  const hasSubcategories: boolean =
    (subcategories?.allIssueSubcategories?.edges?.length ?? 0) > 0

  useEffect(() => {
    validateForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSubcategories, fieldConfig.version])

  function handleData(data: IssueQuery | undefined) {
    if (!data) return

    const { issue } = data
    const reference = issue.referenceId
    const date = format(new Date(issue.timeOfIssueStart), 'PP')

    setOverride(issue.id, reference ? `${reference} (${date})` : date)

    setCoordinates({
      lat: issue.locationLatitude,
      lng: issue.locationLongitude,
    })

    const values: EditIssueForm = {
      user: issue.user?.id ?? null,
      responsibleUser: issue.responsibleUser?.id ?? null,
      caseHandler: issue.caseHandler?.id ?? null,
      closed: issue.closedAt !== null,
      archived: issue.archivedAt !== null,
      category: issue.category?.id ?? null,
      subcategory: issue.subcategory?.id ?? null,
      date: new Date(issue.timeOfIssueStart),
      dateEnd: issue.timeOfIssueEnd
        ? new Date(issue.timeOfIssueEnd)
        : new Date(),

      placename: issue.locationPlacename,
      regNr: issue.vehiclePlateNumber ?? '',
      title: issue.title ?? '',
      description: issue.issueDescription,
      damage: issue.damageDescription,
      reason: issue.reason ?? '',
      locationDescription: issue.locationDescription,
      immediateAction: issue.immediateAction ?? '',
      action: issue.action ?? '',

      estimatedCost: issue.costEstimation,
      suggestedSolution: issue.solutionSuggestion,
      actualCost: issue.actualCost,
      solution: issue.solution,
      severity: issue.severity,
      riskAspect: issue.riskAspect?.id ?? null,
      processChoice: issue.processChoice?.id ?? null,
      relationOptions: buildInitialRelationOptions(
        issue.relationOptions?.edges
      ),
      tags: (issue.tags?.edges ?? []).map(
        (e: { node: { id: string } }) => e.node.id
      ),
    }
    updateForm(values)
    updateInitialValues(values)
  }

  const { data: riskAspectData } = useQuery<
    AllIssueRiskAspectsQuery,
    AllIssueRiskAspectsQueryVariables
  >(ALL_ISSUE_RISK_ASPECTS_QUERY)

  const hasRiskAspects: boolean =
    (riskAspectData?.allIssueRiskAspects.edges.length ?? 0) > 0

  const mutations = usePatchIssueMutation()
  const { exportIssue, loading: emailLoading } = useExportIssuesMutations()
  const relationsValid = useRequiredRelationsSatisfied(
    form.relationOptions,
    'edit'
  )

  useEffect(() => {
    if (data) return
    setOverride(issueId, translations.loading)
  }, [data, issueId, setOverride, translations.loading])

  const issue = useMemo(() => data?.issue, [data])
  const attachments = useMemo(() => issue?.attachments ?? [], [issue])

  useEffect(() => {
    if (!issue) return

    if (!editing) {
      setCoordinates({
        lat: issue.locationLatitude,
        lng: issue.locationLongitude,
      })
    }
  }, [editing, issue])

  function handleLocationUpdate(
    results: google.maps.GeocoderResult[] | null,
    status: google.maps.GeocoderStatus
  ) {
    if (!editing || status !== google.maps.GeocoderStatus.OK || !results) return
    const [place] = results
    if (!place || !place.formatted_address) return

    const [lat, lng] = [
      place.geometry.location.lat(),
      place.geometry.location.lng(),
    ]
    setCoordinates({
      lat,
      lng,
    })

    updateForm({
      placename: place.formatted_address,
    })
  }
  function handleAttachmentsUpdate(add: FileAttachment[], remove: string[]) {
    setAttachmentsAdd(
      add.map(attachment => ({
        name: attachment.name,
        file: attachment.file,
      }))
    )
    setattachmentsRemove(remove)
  }

  async function doExport(values: ExportIssueForm) {
    if (!issue) return

    // Export is async (Celery): the EXPORTED action log is written by the
    // worker after the mutation returns. Poll until the log count grows.
    const baselineCount = issue.actionLogs?.length ?? 0

    const res = await exportIssue({
      variables: {
        userId: me.id,
        reportId: issue?.id,
        recipients: uniq(values.recipients),
        exportFormat: values.exportFormat,
        exportType: values.exportFormat,
        exportOption: values.exportOption,
        customExportText: values.customExportText,
      },
    })

    // No log will be written if the export didn't start; skip the poll.
    if (!res.data?.exportIssue?.ok) return

    // Mailed exports (PDF render + send) can take a while, so poll well past
    // the mutation's return before giving up (~30s).
    await pollForNewActionLog(refetch, handleData, baselineCount, {
      maxRetries: 10,
      delayMs: 3000,
      isActive: () => isMountedRef.current,
    })
  }

  async function handleExport() {
    if (!issue) return

    const { data } = await addPrompt<ExportIssueForm | null>(resolve => (
      <ExportIssuePrompt
        onSubmit={resolve}
        user={me}
        defaultText={issue.category?.defaultExportText}
      />
    ))
    if (!data) return
    await doExport(data)
  }

  function handleCancel() {
    resetForm()
    setEditing(false)

    if (!attachmentsRef.current) return
    attachmentsRef.current.resetEditedAttachments()
  }

  async function refreshIssue() {
    const { data: newData } = await refetch()
    handleData(newData)
  }

  function handleSubmit(values: EditIssueForm) {
    if (!issue || !values.category) return

    if (typeof google === 'undefined')
      throw new Error('Issue submit: google is undefined')

    async function runMutation(input: PatchIssueInput) {
      if (!issue) return

      try {
        await mutations.patch({
          variables: { id: issue.id, input },
        })
        await refreshIssue()
        handleCancel()
      } catch (error) {
        console.error('Issue submit error:', error)
      }
    }

    let input: PatchIssueInput = {
      responsibleUser: values.responsibleUser,
      caseHandler: values.caseHandler,
      category: values.category,
      subcategory: values.subcategory,
      timeOfIssueStart: formatISO(values.date),
      timeOfIssueEnd: formatISO(values.dateEnd),

      locationPlacename: values.placename,
      locationDescription: values.locationDescription,

      vehiclePlateNumber: values.regNr,
      title: values.title,
      issueDescription: values.description,
      damageDescription: values.damage,
      reason: values.reason,
      immediateAction: values.immediateAction,
      action: values.action,

      costEstimation: values.estimatedCost,
      solutionSuggestion: values.suggestedSolution,
      actualCost: values.actualCost,
      solution: values.solution ?? '',

      attachmentsAdd,
      attachmentsRemove,
      severity: values.severity,
      riskAspect: values.riskAspect,
      processChoice: values.processChoice,
      relationOptions: serializeRelationOptions(values.relationOptions),
      tags: values.tags,
    }

    // Patch semantics: omitting a hidden field leaves its stored value alone
    // rather than overwriting it with a placeholder.
    input = omitHiddenIssueInput(fieldConfig, 'edit', input)

    if (issue.closedAt === null && values.closed)
      input = {
        ...input,
        closedAt: formatISO(new Date()),
        closedBy: me.id,
      }
    else if (!values.closed)
      input = {
        ...input,
        closedAt: null,
        closedBy: null,
      }

    if (coordinates.lat === null || coordinates.lng === null) {
      const service = new google.maps.Geocoder()
      void service.geocode(
        {
          address: values.placename,
        },
        (results, status) => {
          const [place] = results ?? [null]
          if (status !== google.maps.GeocoderStatus.OK || !place) {
            void runMutation(input)
            return
          }

          const [lat, lng] = [
            place.geometry.location.lat(),
            place.geometry.location.lng(),
          ]
          input = {
            ...input,
            locationLatitude: lat,
            locationLongitude: lng,
          }
          void runMutation(input)
        }
      )
    } else {
      input = {
        ...input,
        locationLatitude: coordinates.lat,
        locationLongitude: coordinates.lng,
      }
      void runMutation(input)
    }
  }

  // Cards that hold nothing but hidden fields should not be drawn at all.
  const showTitleCard =
    fieldConfig.isVisible(IssueFormField.TITLE, 'edit') ||
    fieldConfig.isVisible(IssueFormField.TAGS, 'edit')
  const showMap = fieldConfig.isVisible(
    IssueFormField.LOCATION_PLACENAME,
    'edit'
  )

  const isLoading = queryLoading || mutations.loading || emailLoading
  const isFormEdited = useMemo(
    () => formEdited || !!attachmentsAdd.length || !!attachmentsRemove.length,
    [formEdited, attachmentsAdd.length, attachmentsRemove.length]
  )

  const editPermission = PERMISSIONS.issues.change.issue
  const { hasPermissions } = usePermissions()
  const canEditRelations = editing && hasPermissions(editPermission)

  function handleSetRelation(relationId: string, optionIds: string[]) {
    updateForm({
      relationOptions: { ...form.relationOptions, [relationId]: optionIds },
    })
  }

  const tagEdges = issue?.tags?.edges ?? []

  return (
    <Wrapper>
      <header className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
        <div className="min-w-0">
          <H2>
            {issue?.title?.trim()
              ? issue.title
              : `${translations.issue}${
                  issue?.referenceId ? ` ${issue.referenceId}` : ''
                }`}
          </H2>
          {!editing && tagEdges.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tagEdges.map(edge => (
                <TagBadge key={edge.node.id} tag={edge.node} variant="pill" />
              ))}
            </div>
          )}
        </div>
        <div className="fixed inset-x-0 bottom-0 z-40 flex justify-end gap-2 border-t border-stone-200 bg-white p-3 md:static md:z-auto md:flex-wrap md:border-0 md:bg-transparent md:p-0">
          {!editing && (
            <PermissionsRequired
              permissions={PERMISSIONS.companies.export.exportReports}
            >
              <Button
                disabled={isLoading || !issue}
                padding="0 1.5rem"
                height="2.75rem"
                fontSize="1rem"
                onClick={handleExport}
              >
                <Icon icon="inbox-in" fixedWidth margin="0 6px 0 0" />
                {translations.export}
              </Button>
            </PermissionsRequired>
          )}

          {editing && (
            <span className="hidden md:contents">
              <Button
                disabled={isLoading}
                background="gray8"
                height="2.75rem"
                fontSize="1rem"
                onClick={handleCancel}
              >
                {translations.cancel}
              </Button>
            </span>
          )}
          <PermissionsRequired permissions={editPermission}>
            <Button
              disabled={
                isLoading ||
                (editing && (!formValid || !isFormEdited || !relationsValid)) ||
                issue?.closedAt !== null
              }
              height="2.75rem"
              fontSize="1rem"
              onClick={editing ? submit(handleSubmit) : () => setEditing(true)}
            >
              {editing ? translations.save : translations.edit}
            </Button>
          </PermissionsRequired>
        </div>
      </header>

      <Content>
        {isLoading && (
          <Loading>
            <CenteredLoader marginTop="0" size={72} thickness={8} />
          </Loading>
        )}

        <MainColumn>
          {editing && showTitleCard && (
            <CardTitle>
              <div className="inner">
                {fieldConfig.isVisible(IssueFormField.TITLE, 'edit') && (
                  <>
                    <h2>
                      {fieldConfig.labelFor(
                        IssueFormField.TITLE,
                        translations.title
                      )}
                      {fieldConfig.isRequired(IssueFormField.TITLE, 'edit') && (
                        <span className="ml-1 text-rose-500">*</span>
                      )}
                    </h2>
                    <Input
                      value={form.title}
                      error={errors.title}
                      fullWidth
                      grayed
                      height="3rem"
                      onChange={handler('title')}
                    />
                  </>
                )}
                {fieldConfig.isVisible(IssueFormField.TAGS, 'edit') && (
                  <div className="mt-3">
                    <IssueTagPicker
                      value={form.tags}
                      onChange={tags => updateForm({ tags })}
                    />
                  </div>
                )}
              </div>
            </CardTitle>
          )}

          <Details
            loading={queryLoading}
            editing={editing}
            form={form}
            errors={errors}
            fieldConfig={fieldConfig}
            placename={issue?.locationPlacename}
            regNr={issue?.vehiclePlateNumber}
            damage={issue?.damageDescription}
            reason={issue?.reason}
            immediateAction={issue?.immediateAction}
            action={issue?.action}
            description={issue?.issueDescription}
            locationDescription={issue?.locationDescription}
            costEstimate={issue?.costEstimation}
            actualCost={issue?.actualCost}
            suggestedSolution={issue?.solutionSuggestion}
            solution={issue?.solution}
            relatedLink={issue?.relatedLink}
            onLocationUpdate={handleLocationUpdate}
            onChange={handler}
          />

          {fieldConfig.isVisible(IssueFormField.ATTACHMENTS, 'edit') && (
            <Attachments
              ref={attachmentsRef}
              editing={editing}
              attachments={attachments}
              onUpdate={handleAttachmentsUpdate}
            />
          )}
        </MainColumn>

        <Rail>
          {showMap && (
            <Map
              editing={editing}
              latitude={coordinates.lat}
              longitude={coordinates.lng}
              onClick={handleLocationUpdate}
            />
          )}

          <MainInfo
            subcategoryOptions={(
              subcategories?.allIssueSubcategories?.edges ?? []
            )?.map(el => ({
              label: el?.node?.name ?? '',
              value: el?.node?.id ?? '',
            }))}
            subcategoriesLoading={subcategoriesLoading}
            loading={queryLoading}
            editing={editing}
            form={form}
            errors={errors}
            subCategory={issue?.subcategory}
            category={issue?.category}
            date={issue?.timeOfIssueStart}
            closedAt={issue?.closedAt}
            archivedAt={issue?.archivedAt}
            reference={issue?.referenceId}
            user={issue?.user}
            responsible={issue?.responsibleUser}
            caseHandler={issue?.caseHandler}
            severity={issue?.severity}
            riskAspect={issue?.riskAspect}
            dateEnd={issue?.timeOfIssueEnd}
            fieldConfig={fieldConfig}
            hasRiskAspects={hasRiskAspects}
            onChange={handler}
            updateForm={updateForm}
          />

          <IssueRelationsPanel
            value={form.relationOptions}
            editable={canEditRelations}
            mode="edit"
            onChange={handleSetRelation}
          />
        </Rail>
      </Content>

      <IssueActionLogTimeline
        logs={issue?.actionLogs ?? []}
        loading={queryLoading}
        error={!!queryError}
      />
    </Wrapper>
  )
}
